import { Collapse } from "components/ui/collapse/Collapse"
import type { CollapseProps } from "antd"

type TCollapseItem = NonNullable<CollapseProps["items"]>[number] & { "data-testid"?: string }

type Props = {
	items: TCollapseItem[]
	/** True while a search term is active; every panel is then opened. */
	isExpanded: boolean
	/** Part of the remount key, so a new term re-applies the open state. */
	expandKey?: string
	className?: string
}

/**
 * A Collapse that opens every one of its panels while a search is active.
 *
 * Needed for the inner levels of the configuration pages. Their data is filtered to
 * matching rows before rendering, so each surviving panel contains a hit — but the panel
 * holding the table sits one or two levels down, and opening only the outermost level
 * left the matches hidden behind a closed panel.
 *
 * The `key` forces a remount because Ant Design reads `defaultActiveKey` only on mount
 * and the shared Collapse wrapper exposes no controlled `activeKey`.
 */
export const AutoExpandCollapse = ({ items, isExpanded, expandKey = "", className }: Props) => (
	<Collapse
		key={isExpanded ? `expanded-${expandKey}` : "collapsed"}
		items={items}
		className={className}
		defaultActiveKey={isExpanded ? items.map((item) => item.key as string) : undefined}
	/>
)
